import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Chip,
  Alert,
  Snackbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import KeyIcon from "@mui/icons-material/VpnKey";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "context/AuthContext";
import {
  getUsersCheckoutCredentials,
  saveUserCheckoutCredentials,
  type UserCheckoutCredential,
} from "api/order-entry.api";

export default function CheckoutCredentialsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "super-admin";
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<UserCheckoutCredential | null>(null);
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const { data, isLoading } = useQuery({
    queryKey: ["users-checkout-credentials"],
    queryFn: () => getUsersCheckoutCredentials().then((r) => r.data),
    enabled: isAdmin,
  });

  // Prefill the dialog when an admin opens it for a user.
  useEffect(() => {
    if (editing) {
      setLoginId(editing.loginId ?? "");
      setPassword("");
      setShowPassword(false);
    }
  }, [editing]);

  const mutation = useMutation({
    mutationFn: () =>
      saveUserCheckoutCredentials(editing!.userId, {
        loginId: loginId.trim(),
        password: password ? password : undefined,
      }),
    onSuccess: (res) => {
      setSnackbar({
        open: true,
        message: res.data.message || "Credentials saved",
        severity: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["users-checkout-credentials"] });
      setEditing(null);
    },
    onError: (err: any) => {
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to save credentials",
        severity: "error",
      });
    },
  });

  if (!isAdmin) {
    return (
      <Box>
        <Alert
          severity="error"
          action={
            <Typography
              component="span"
              sx={{ cursor: "pointer", fontWeight: 600 }}
              onClick={() => navigate("/")}
            >
              Go back
            </Typography>
          }
        >
          You don't have access to this page.
        </Alert>
      </Box>
    );
  }

  const rows = (data?.users ?? []).map((u: UserCheckoutCredential) => ({ id: u.userId, ...u }));

  const columns: GridColDef[] = [
    { field: "name", headerName: "User", flex: 1, minWidth: 180 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 220 },
    { field: "role", headerName: "Role", width: 120 },
    {
      field: "hasCredentials",
      headerName: "Credentials",
      width: 150,
      renderCell: (params) =>
        params.value ? (
          <Chip label="Set" color="success" size="small" variant="outlined" />
        ) : (
          <Chip label="Not set" color="default" size="small" variant="outlined" />
        ),
    },
    {
      field: "loginId",
      headerName: "Login ID",
      width: 160,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "updatedAt",
      headerName: "Updated",
      width: 170,
      renderCell: (params) =>
        params.value ? dayjs(params.value).format("MMM D, YYYY h:mm A") : "-",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          startIcon={<KeyIcon />}
          onClick={() => setEditing(params.row as UserCheckoutCredential)}
        >
          {params.row.hasCredentials ? "Edit" : "Set"}
        </Button>
      ),
    },
  ];

  const editingHasCreds = editing?.hasCredentials ?? false;

  const handleSave = () => {
    if (!loginId.trim()) {
      setSnackbar({ open: true, message: "Login ID is required", severity: "error" });
      return;
    }
    if (!editingHasCreds && !password) {
      setSnackbar({ open: true, message: "Password is required", severity: "error" });
      return;
    }
    mutation.mutate();
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={1}>
        User CheckoutChamp Credentials
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Set each user's CheckoutChamp API login and password. These are used as
        that user's credentials when they create an order entry. Users without
        credentials fall back to the system default.
      </Typography>

      <DataGrid
        rows={rows}
        columns={columns}
        loading={isLoading}
        disableRowSelectionOnClick
        autoHeight
        initialState={{
          pagination: { paginationModel: { pageSize: 25, page: 0 } },
        }}
        pageSizeOptions={[25, 50, 100]}
        sx={{
          "& .MuiDataGrid-columnHeaders": { bgcolor: "background.paper" },
        }}
      />

      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          CheckoutChamp credentials — {editing?.name}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="CheckoutChamp Login ID"
            value={loginId}
            onChange={(e) => setLoginId(e.target.value)}
            fullWidth
            size="small"
            sx={{ mt: 1, mb: 2 }}
            autoComplete="off"
          />
          <TextField
            label="CheckoutChamp Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            size="small"
            type={showPassword ? "text" : "password"}
            placeholder={
              editingHasCreds ? "•••••••• (leave blank to keep current)" : ""
            }
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((s) => !s)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? (
                      <VisibilityOffIcon fontSize="small" />
                    ) : (
                      <VisibilityIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={mutation.isPending}
            startIcon={
              mutation.isPending ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
          >
            {mutation.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
